import { create } from 'zustand'
import { authApi } from '@/services/api'

interface Notification {
  id: string
  title: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  read: boolean
  created_at: string
  link?: string
}

interface NotificationState {
  notifications: Notification[]
  unreadCount: number
  isLoading: boolean

  // Actions
  fetchNotifications: () => Promise<void>
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt'>) => void
  clearNotifications: () => void
}

export const useNotificationStore = create<NotificationState>()((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,

  fetchNotifications: async () => {
    set({ isLoading: true })
    try {
      const response = await authApi.getNotifications()
      const notifications = response.data
      const unreadCount = notifications.filter((n: Notification) => !n.read).length

      set({
        notifications,
        unreadCount,
        isLoading: false
      })
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
      set({ isLoading: false })
    }
  },

  markAsRead: (id: string) => {
    const { notifications } = get()
    const updatedNotifications = notifications.map(n =>
      n.id === id ? { ...n, read: true } : n
    )
    const unreadCount = updatedNotifications.filter(n => !n.read).length

    set({
      notifications: updatedNotifications,
      unreadCount
    })

    // Update on server
    authApi.markNotificationAsRead(id).catch(console.error)
  },

  markAllAsRead: () => {
    const { notifications } = get()
    const updatedNotifications = notifications.map(n => ({ ...n, read: true }))

    set({
      notifications: updatedNotifications,
      unreadCount: 0
    })

    // Update on server
    authApi.markAllNotificationsAsRead().catch(console.error)
  },

  addNotification: (notification) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      created_at: new Date().toISOString(),
      read: false,
    }

    const { notifications } = get()
    set({
      notifications: [newNotification, ...notifications],
      unreadCount: get().unreadCount + 1
    })
  },

  clearNotifications: () => {
    set({
      notifications: [],
      unreadCount: 0
    })
  },
}))